import React, { useEffect, useId, useRef, useState } from "react";
import moment from "moment";
import { faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import useTranslation from "../../../hooks/useTranslation";
import {
    Annotation,
    useGetAnnotationsQuery,
} from "../../../../services/dataEntering/annotations/api";
import { ToolbarPillButton } from "./Toolbar";

interface Props {
    accountId: number | null;
    documentId: number | null;
    note?: string | null;
    reserveTop?: string;
    layerClassName?: string;
}

function authorName(annotation: Annotation, t: (key: string) => string): string {
    if (annotation.source === "auto") return t("resources.annotations.auto_booking_author");
    return annotation.author?.username ?? t("resources.annotations.deleted_author");
}

export default function AnnotationsPanel({
    accountId,
    documentId,
    note,
    reserveTop,
    layerClassName = "z-20",
}: Props) {
    const { t, locale } = useTranslation();
    const noteLabelId = useId();
    const [open, setOpen] = useState(true);
    const bottomRef = useRef<HTMLDivElement>(null);

    const { currentData: annotations = [] } = useGetAnnotationsQuery(
        { accountId, documentId },
        { skip: !accountId || !documentId },
    );

    // Scroll the list to the newest annotation when one arrives or the panel reopens —
    // but not on unrelated re-renders, so it doesn't yank the user away mid-read.
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "instant" });
    }, [annotations.length, open]);

    const hasAnnotations = annotations.length > 0;
    const hasNote = !!note?.trim();
    if (!hasAnnotations && !hasNote) return null;

    return (
        <div
            style={reserveTop ? { top: `min(${reserveTop}, 50%)` } : undefined}
            className={`pointer-events-none absolute inset-x-4 bottom-4 flex flex-col items-start justify-end gap-2 md:right-auto ${layerClassName}`}
        >
            {open && (
                <div
                    className={`pointer-events-auto flex w-full flex-col overflow-hidden rounded-xl border border-border bg-surface/95 text-ink shadow-lg backdrop-blur md:w-96 md:max-w-[40vw] ${
                        reserveTop ? "max-h-[calc(100%-3.5rem)]" : "max-h-[60vh]"
                    }`}
                >
                    {hasNote && (
                        <div
                            className={`flex max-h-64 min-h-0 flex-col overflow-hidden bg-accent/10 px-4 py-3 ${
                                hasAnnotations ? "border-b border-border" : ""
                            }`}
                        >
                            <div
                                id={noteLabelId}
                                className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-accent"
                            >
                                {t("resources.uploaded_documents.fields.note")}
                            </div>
                            <div
                                role="region"
                                aria-labelledby={noteLabelId}
                                className="min-h-5 overflow-y-auto whitespace-pre-wrap break-words text-sm text-ink"
                            >
                                {note}
                            </div>
                        </div>
                    )}
                    {hasAnnotations && (
                        <div className="flex flex-col overflow-hidden">
                            <div className="border-b border-border px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                                {t("resources.annotations.name")}
                            </div>
                            <div className="flex max-h-64 flex-col gap-3 overflow-auto px-4 py-3">
                                {annotations.map((a) => (
                                    <div key={a.id} className="flex flex-col gap-1">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-sm font-medium text-ink">
                                                    {authorName(a, t)}
                                                </span>
                                                {a.author && (
                                                    <span className="rounded-pill bg-surface-3 px-1.5 py-0.5 text-[10px] text-ink-muted">
                                                        {t(`attributes.user.role.${a.author.role}`)}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-[10px] text-ink-muted">
                                                {moment(a.createdAt).locale(locale).fromNow()}
                                            </span>
                                        </div>
                                        <div className="whitespace-pre-wrap break-words text-sm text-ink">
                                            {a.content}
                                        </div>
                                    </div>
                                ))}
                                <div ref={bottomRef} />
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="pointer-events-auto">
                <ToolbarPillButton
                    icon={faNoteSticky}
                    title={t("actions.pdf.toggle_annotations")}
                    onClick={() => setOpen((v) => !v)}
                    variant="accent"
                    ariaExpanded={open}
                />
            </div>
        </div>
    );
}
